'use client';

import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Link as MuiLink, Button, Paper, TextField } from '@mui/material';
import { SUPPORT_EMAIL, DEFAULT_SITE_URL } from '@/lib/site-contact';

const PrivacyPage = () => {
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const extractHeadings = () => {
      const headingElements = Array.from(document.querySelectorAll('h2, h3, h4')); // Include h3 and h4
      const extracted = headingElements.map((heading, index) => {
        const id = heading.textContent ? heading.textContent.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, '') : `section-${index}`;
        heading.id = id;
        return { id, text: heading.textContent || '' };
      });
      setHeadings(extracted);
    };

    extractHeadings();
  }, []);

  const lastUpdated = "November 8, 2025"; // Manually set for now

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const { generatePdfFromElement } = await import('@/lib/pdf/pdfFacade');
    const content = document.getElementById('privacy-content');
    if (content) {
      const pdf = await generatePdfFromElement(content);
      pdf.save('floai-privacy-policy.pdf');
    }
  };

  const highlightText = (text: string) => {
    if (!searchTerm) return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
      <>
        {parts.map((part, i) => (
          <span
            key={i}
            style={part.toLowerCase() === searchTerm.toLowerCase() ? { backgroundColor: 'yellow', fontWeight: 'bold' } : {}}
          >
            {part}
          </span>
        ))}
      </>
    );
  };

  return (
    <PublicPageLayout>
      <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, py: 4, gap: 4 }}>
        <Box sx={{ flexGrow: 1, pr: { xs: 0, md: 3 } }} id="privacy-content">
          <Typography variant="h4" component="h1" fontWeight={700} mb={2} color="text.primary">
            Privacy Policy
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" mb={4}>
            Last Updated: {lastUpdated}
          </Typography>

          <TextField
            label="Search Privacy Policy"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 4 }}
          />

          <h2>{highlightText("Your Privacy Matters to Us")}</h2>
          <p>{highlightText("This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.")}</p>

          <h2>{highlightText("Interpretation and Definitions")}</h2>
          <h3>{highlightText("Interpretation")}</h3>
          <p>{highlightText("The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.")}</p>

          <h3>{highlightText("Definitions")}</h3>
          <Box sx={{ pl: 5 }}>
            <ul>
              <li><strong>{highlightText("Account")}</strong> {highlightText("means a unique account created for You to access our Service or parts of our Service.")}</li>
              <li><strong>{highlightText("Company")}</strong> {highlightText("(referred to as either \"the Company\", \"We\", \"Us\" or \"Our\" in this Agreement) refers to FloAI.")}</li>
              <li><strong>{highlightText("Cookies")}</strong> {highlightText("are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.")}</li>
              <li><strong>{highlightText("Country")}</strong> {highlightText("refers to: Illinois, United States")}</li>
              <li><strong>{highlightText("Device")}</strong> {highlightText("means any device that can access the Service such as a computer, a cellphone or a digital tablet.")}</li>
              <li><strong>{highlightText("Personal Data")}</strong> {highlightText("is any information that relates to an identified or identifiable individual.")}</li>
              <li><strong>{highlightText("Service")}</strong> {highlightText("refers to the Website.")}</li>
              <li><strong>{highlightText("Service Provider")}</strong> {highlightText("means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.")}</li>
              <li><strong>{highlightText("Usage Data")}</strong> {highlightText("refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).")}</li>
              <li><strong>{highlightText("Website")}</strong> {highlightText("refers to FloAI, accessible from [Your Website Address]")}</li>
              <li><strong>{highlightText("You")}</strong> {highlightText("means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.")}</li>
            </ul>
          </Box>

          <h2>{highlightText("Collecting and Using Your Personal Data")}</h2>
          <h3>{highlightText("Types of Data Collected")}</h3>
          <h4>{highlightText("Personal Data")}</h4>
          <p>{highlightText("While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:")}</p>
          <Box sx={{ pl: 5 }}>
            <ul>
              <li>{highlightText("Email address")}</li>
              <li>{highlightText("First name and last name")}</li>
              <li>{highlightText("Phone number")}</li>
              <li>{highlightText("Address, State, Province, ZIP/Postal Code, City")}</li>
              <li>{highlightText("Usage Data")}</li>
            </ul>
          </Box>

          <h4>{highlightText("Usage Data")}</h4>
          <p>{highlightText("Usage Data is collected automatically when using the Service.")}</p>

          <p>{highlightText("Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.")}</p>

          <p>{highlightText("When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.")}</p>

          <p>{highlightText("We may also collect information that Your browser sends whenever You visit our Service or when You access the Service by or through a mobile device.")}</p>

          <h2>{highlightText("Use of Your Personal Data")}</h2>
          <p>{highlightText("The Company may use Personal Data for the following purposes:")}</p>
          <Box sx={{ pl: 5 }}>
            <ul>
              <li><strong>{highlightText("To provide and maintain our Service")}</strong>, {highlightText("including to monitor the usage of our Service.")}</li>
              <li><strong>{highlightText("To manage Your Account:")}</strong> {highlightText("to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user.")}</li>
              <li><strong>{highlightText("For the performance of a contract:")}</strong> {highlightText("the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.")}</li>
              <li><strong>{highlightText("To contact You:")}</strong> {highlightText("To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application's push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation.")}</li>
              <li><strong>{highlightText("To provide You")}</strong> {highlightText("with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless You have opted not to receive such information.")}</li>
              <li><strong>{highlightText("To manage Your requests:")}</strong> {highlightText("To attend and manage Your requests to Us.")}</li>
              <li><strong>{highlightText("For business transfers:")}</strong> {highlightText("We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred.")}</li>
              <li><strong>{highlightText("For other purposes")}</strong>: {highlightText("We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience.")}</li>
            </ul>
          </Box>

          <h2>{highlightText("Disclosure of Your Personal Data")}</h2>
          <h3>{highlightText("With Service Providers:")}</h3>
          <p>{highlightText("We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You.")}</p>
          <h3>{highlightText("For Business Transfers:")}</h3>
          <p>{highlightText("We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.")}</p>
          <h3>{highlightText("With Affiliates:")}</h3>
          <p>{highlightText("We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us.")}</p>
          <h3>{highlightText("With Business Partners:")}</h3>
          <p>{highlightText("We may share Your information with Our business partners to offer You certain products, services or promotions.")}</p>
          <h3>{highlightText("With other users:")}</h3>
          <p>{highlightText("When You share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.")}</p>
          <h3>{highlightText("With Your consent:")}</h3>
          <p>{highlightText("We may disclose Your personal information for any other purpose with Your consent.")}</p>

          <h2>{highlightText("Retention of Your Personal Data")}</h2>
          <p>{highlightText("The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.")}</p>

          <p>{highlightText("The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of Our Service, or We are legally obligated to retain this data for longer time periods.")}</p>

          <h2>{highlightText("Transfer of Your Personal Data")}</h2>
          <p>{highlightText("Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of Your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.")}</p>

          <p>{highlightText("Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer.")}</p>

          <p>{highlightText("The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy and no transfer of Your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of Your data and other personal information.")}</p>

          <h2>{highlightText("Delete Your Personal Data")}</h2>
          <p>{highlightText("You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You.")}</p>

          <p>{highlightText("Our Service may provide You with the ability to delete certain information about You from within the Service.")}</p>

          <p>{highlightText("You may update Your information at any time by signing in to Your Account, if you have one, and visiting the account settings section that allows You to manage Your Personal Data. You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us.")}</p>

          <p>{highlightText("Please note that We may need to retain certain information when we have a legal obligation or lawful basis to do so.")}</p>

          <h2>{highlightText("Disclosure of Your Personal Data (Law Enforcement)")}</h2>
          <h3>{highlightText("Law enforcement")}</h3>
          <p>{highlightText("Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).")}</p>
          <h3>{highlightText("Other legal requirements")}</h3>
          <p>{highlightText("The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:")}</p>
          <Box sx={{ pl: 5 }}>
            <ul>
              <li>{highlightText("Comply with a legal obligation")}</li>
              <li>{highlightText("Protect and defend the rights or property of the Company")}</li>
              <li>{highlightText("Prevent or investigate possible wrongdoing in connection with the Service")}</li>
              <li>{highlightText("Protect the personal safety of Users of the Service or the public")}</li>
              <li>{highlightText("Protect against legal liability")}</li>
            </ul>
          </Box>

          <h2>{highlightText("Security of Your Personal Data")}</h2>
          <p>{highlightText("The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.")}</p>

          <h2>{highlightText("Children's Privacy")}</h2>
          <p>{highlightText("Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.")}</p>

          <p>{highlightText("If We need to rely on consent as a legal basis for processing Your information and Your country requires consent from a parent, We may require Your parent's consent before We collect and use that information.")}</p>

          <h2>{highlightText("Links to Other Websites")}</h2>
          <p>{highlightText("Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.")}</p>

          <p>{highlightText("We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.")}</p>

          <h2>{highlightText("Changes to this Privacy Policy")}</h2>
          <p>{highlightText("We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.")}</p>

          <p>{highlightText("We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the \"Last updated\" date at the top of this Privacy Policy.")}</p>

          <p>{highlightText("You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.")}</p>

          <h2>{highlightText("Contact Us")}</h2>
          <p>{highlightText("If you have any questions about this Privacy Policy, You can contact us:")}</p>
          <Box sx={{ pl: 5 }}>
            <ul>
              <li>{highlightText(`By email: ${SUPPORT_EMAIL}`)}</li>
              <li>{highlightText(`By visiting this page on our website: ${DEFAULT_SITE_URL}/contact`)}</li>
            </ul>
          </Box>

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={handlePrint}>Print</Button>
            <Button variant="contained" onClick={handleDownloadPDF}>Download PDF</Button>
          </Box>

        </Box>

        <Box sx={{ width: { xs: '100%', md: 250 }, flexShrink: 0 }}>
          <Paper elevation={3} sx={{ position: 'sticky', top: 100, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              On this page
            </Typography>
            <Box component="nav">
              {headings.map((heading) => (
                <MuiLink
                  key={heading.id}
                  href={`#${heading.id}`}
                  sx={{
                    display: 'block',
                    py: 0.5,
                    pl: 1,
                    textDecoration: 'none',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                    borderLeft: '2px solid transparent',
                    '&.active': { borderLeftColor: 'primary.main', color: 'primary.main', fontWeight: 600 },
                  }}
                >
                  {heading.text}
                </MuiLink>
              ))}
            </Box>
          </Paper>
        </Box>
      </Container>
    </PublicPageLayout>
  );
};

export default PrivacyPage;
